import { Box, CircularProgress, Skeleton } from "@material-ui/core";
import { forwardRef, useState, useEffect } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import type { BoxProps } from "@material-ui/system";

type LazyLoadImageProps = {
  src: string;
  alt?: string;
  effect?: string;
  disabledEffect?: boolean;
};

export type CropToImageProps = {
  cropTopImage?: boolean;
};

type ImageProps = BoxProps & LazyLoadImageProps & CropToImageProps;

const TOP_CROP_SIZE_PIXELS = 64;

export const Image = forwardRef<HTMLSpanElement, ImageProps>(
  ({ disabledEffect = false, effect = "blur", sx, cropTopImage, src, alt, ...other }, ref) => {
    const [isLoading, setIsLoading] = useState(true);
    // The skeleton animates only once the image is actually loading — it came into view
    // (`beforeLoad`). An image that is never in view never loads, and its skeleton animated
    // forever: every hidden slide of a carousel (react-slick clones each slide too). On the
    // listings match page that was ~14 endless animations per result, and at 100 results the
    // page spent 96% of its time redrawing them while nobody touched it (2026-10-28).
    const [hasStartedLoading, setHasStartedLoading] = useState(false);

    // Reset loading state when src changes (for edit transitions)
    useEffect(() => {
      setIsLoading(true);
    }, [src]);

    // For creative result images, use spinner; for others use skeleton
    const isCreativeResultImage =
      alt?.includes("Generated Creative") || alt?.includes("Creative Preview");

    const renderLoadingIndicator = () => {
      if (!isLoading) return null;

      if (isCreativeResultImage) {
        return (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              zIndex: 2
            }}
          >
            <CircularProgress size={50} />
          </Box>
        );
      }

      return (
        <Skeleton
          variant="rectangular"
          animation={hasStartedLoading ? undefined : false}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1
          }}
        />
      );
    };

    const content = (
      <Box
        component={LazyLoadImage}
        wrapperClassName="wrapper"
        effect={disabledEffect ? undefined : effect}
        placeholderSrc={disabledEffect ? "/static/transparent.png" : "/static/placeholder.svg"}
        src={src}
        alt={alt}
        beforeLoad={() => setHasStartedLoading(true)}
        afterLoad={() => setIsLoading(false)}
        sx={{
          width: 1,
          objectFit: "cover",
          ...(cropTopImage
            ? {
                height: `calc(100% + ${TOP_CROP_SIZE_PIXELS}px)`,
                objectPosition: `center -${TOP_CROP_SIZE_PIXELS}px`
              }
            : {
                height: 1
              })
        }}
        {...other}
      />
    );

    return (
      <Box
        ref={ref}
        component="span"
        sx={{
          lineHeight: 1,
          display: "block",
          overflow: "hidden",
          position: "relative",
          "& .wrapper": {
            width: 1,
            height: 1,
            backgroundSize: "cover !important"
          },
          ...sx
        }}
      >
        {renderLoadingIndicator()}
        {content}
      </Box>
    );
  }
);
